import { TextVariantWithModifiers } from "@assets/style/appMuiTheme"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoFormControl,
  DiscoFormControlVariant,
  DiscoSection,
  DiscoText,
  DiscoTooltip,
} from "@disco-ui"
import { ClassNameMap } from "@material-ui/styles"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import classNames from "classnames"

export type AIDrawerSectionProp = {
  key: string
  title?: string
  titleTooltip?: string
  description?: React.ReactNode
  label?: React.ReactNode
  labelClassname?: string
  children?: React.ReactNode
  error?: boolean
  errorMessages?: string[]
  tooltip?: string
  tooltipChildren?: React.ReactElement
  variant?: DiscoFormControlVariant
  leftIcon?: React.ReactNode
  leftContent?: React.ReactNode
  rightContent?: React.ReactNode
  classes?: Partial<ClassNameMap<"form">>
}

interface Props {
  classes?: Partial<ClassNameMap<"form" | "label">>
  growLabel?: boolean
  sections: AIDrawerSectionProp[]
  textVariant?: TextVariantWithModifiers
}

function AIDrawerSection({
  textVariant = "body-md-700",
  growLabel = false,
  sections,
  classes: customClasses,
}: Props) {
  const isMobile = useIsMobile()
  const classes = useStyles()
  return (
    <DiscoSection className={classNames(classes.section)}>
      {sections.map(
        (
          {
            key,
            title,
            titleTooltip,
            label,
            description,
            tooltip,
            tooltipChildren,
            error,
            variant = "two-column",
            errorMessages,
            children,
            leftIcon,
            leftContent,
            rightContent,
            classes: sectionCustomClasses,
            labelClassname,
          },
          index
        ) => (
          <DiscoFormControl
            key={key}
            {...(leftIcon || leftContent || rightContent
              ? {
                  label: (
                    <div
                      className={classNames(classes.sourcesName, {
                        [classes.rightContentLabel]: rightContent,
                      })}
                    >
                      {leftIcon && <div className={classes.leftIcon}>{leftIcon}</div>}
                      {leftContent && (
                        <div className={classes.leftContentContainer}>{leftContent}</div>
                      )}
                      <div>
                        <div className={classes.titleContainer}>
                          <DiscoText variant={textVariant}>{title}</DiscoText>
                          {titleTooltip && <DiscoTooltip content={titleTooltip} />}
                        </div>
                        <DiscoText variant={"body-sm"} color={"text.secondary"}>
                          {description}
                        </DiscoText>
                      </div>
                      {!isMobile && rightContent && <div>{rightContent}</div>}
                    </div>
                  ),
                }
              : {
                  title,
                  description,
                  label,
                })}
            variant={variant}
            tooltip={tooltip}
            tooltipChildren={tooltipChildren}
            error={error}
            errorMessages={errorMessages}
            className={classNames(
              classes.form,
              { [classes.formBorder]: index !== 0 },
              customClasses?.form,
              sectionCustomClasses?.form
            )}
            marginBottom={0}
            growLabel={growLabel}
            classes={{
              root: classes.label,
            }}
            labelClasses={{
              root: classNames(
                labelClassname,
                ...ArrayUtils.spreadIf(classes.rightContentLabel, Boolean(rightContent))
              ),
            }}
          >
            {children}
          </DiscoFormControl>
        )
      )}
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  section: {
    boxShadow: theme.palette.groovyDepths.xs,
    border: theme.palette.constants.borderSmall,
    padding: 0,
  },
  form: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
    padding: theme.spacing(2),
  },
  formBorder: {
    borderTop: theme.palette.constants.borderSmall,
  },
  sourcesName: {
    display: "flex",
    gap: theme.spacing(1.5),
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  label: {
    "& > label": {
      marginBottom: 0,
    },
  },
  leftIcon: {
    "& > svg": {
      width: "24px",
      height: "24px",
    },

    height: "48px",
    width: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.groovy.neutral[300]}`,
    borderRadius: theme.measure.borderRadius.medium,
    background:
      theme.palette.type === "dark"
        ? theme.palette.groovy.onDark[500]
        : theme.palette.gradient.ltGrey,
  },
  leftContentContainer: {
    flex: "0 0 auto",
  },
  rightContentLabel: {
    flex: 1,
    justifyContent: "space-between",
  },
}))

export default AIDrawerSection
